import React, { useEffect, useState, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { notification } from "antd";
import LottieLoader from "../../Components/common/LottieLoader";
import {
  PhoneOutlined,
  ArrowRightOutlined,
  AudioOutlined,
  CheckOutlined,
  ExpandOutlined,
  InfoCircleOutlined,
  SoundOutlined,
} from "@ant-design/icons";
import { ThemeModal } from "../Styles/Theme";
import { useConversation } from "@elevenlabs/react";
import axios from "axios";
import config from "../../config";
import {
  getCampaignByLinkApi,
  getCampaignByIdReset,
} from "../../store/actions/campaignAction";
import {
  getRespondentByTransactionIdApi,
  getRespondentReset,
} from "../../store/actions/surveyRespondentAction";
import {
  LandingPageContainer,
  BackgroundContainer,
  LandingPageWrapper,
  Logo,
  ContentCard,
  Title,
  MessageText,
  NextButton,
  ProfileSection,
  AIIcon,
  ProfileName,
  InstructionsText,
  CityButton,
  CityButtonGroup,
  PinSection,
  PinLabel,
  PinBox,
  PinBoxGroup,
  WebCallSection,
  WebCallTitle,
  WebCallSubtitle,
  WebCallButton,
  CallStatusIndicator,
  OrDivider,
  CallOptionsContainer,
  PhoneCallSection,
  ActiveCallCard,
  CallHeader,
  CallAvatar,
  CallHeaderInfo,
  CallStudyName,
  CallPinBadge,
  CallHeaderRight,
  LiveBadge,
  CallContent,
  TimerDisplay,
  WaveformContainer,
  WaveformBar,
  MediaDisplayArea,
  MediaExpandButton,
  MediaSoundButton,
  MediaQuestionText,
  ActionButtonsRow,
  ActionButtonWrapper,
  ActionCircle,
  ActionLabel,
  MutedBubble,
  NoiseBubble,
  MediaPreviewModalStyle,
} from "./CampaignLandingPageStyle";

// Parse prompt_questions XML to build mediaMap: { media_id: { url, type, filename, questionText } }
const buildMediaMap = (promptQuestions) => {
  if (!promptQuestions || !promptQuestions.includes('has_media="true"')) return {};
  const map = {};
  // rule_followup included so media attached to a rule-based follow-up is still
  // displayable to the respondent - without it the media_id would be missing
  // from the map and display_media would fail for that tag.
  const tagRegex = /<(?:question|followup|rule_followup)([^>]*has_media="true"[^>]*)>\s*([\s\S]*?)\s*<(?:break|followup|rule_followup|\/question)/g;
  let match;
  while ((match = tagRegex.exec(promptQuestions)) !== null) {
    const attrs = match[1];
    const questionText = (match[2] || '').trim();
    const get = (name) => {
      const m = attrs.match(new RegExp(`${name}="([^"]*)"`));
      return m ? m[1] : '';
    };
    const mediaId = get('media_id');
    const fileKey = get('media_file_key');
    const mediaType = get('media_type');
    if (mediaId || fileKey) {
      const unescape = (s) => (s || '').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
      let url = unescape(get('media_url'));
      // For YouTube links, recover embed URL from media_id (yt_<videoId>) or media_original_url if url is blank
      if (mediaType === 'link' && !url) {
        const ytIdFromId = mediaId?.startsWith('yt_') ? mediaId.slice(3) : null;
        if (ytIdFromId) {
          url = `https://www.youtube-nocookie.com/embed/${ytIdFromId}?rel=0&modestbranding=1&controls=1`;
        } else {
          const origUrl = unescape(get('media_original_url'));
          const m2 = origUrl?.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
          if (m2) url = `https://www.youtube-nocookie.com/embed/${m2[1]}?rel=0&modestbranding=1&controls=1`;
        }
      }
      map[mediaId || fileKey] = {
        mediaId: mediaId || fileKey,
        url,
        type: mediaType,
        filename: unescape(get('media_filename')),
        questionText,
      };
    }
  }
  return map;
};

// Format seconds to HH:MM:SS
const formatTimer = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Seeded pseudo-random for consistent waveform shape across renders
const _sr = (s) => { const x = Math.sin(s * 9301 + 49297) * 233280; return x - Math.floor(x); };

// Generate waveform bars — tiny dots when idle, animate on voice activity
const WAVE_BARS = Array.from({ length: 80 }, (_, i) => {
  const r = _sr(i + 1);
  return { height: 0.05 + r * 0.04 }; // 5-9% = visible bars when idle
});

// Background-noise pill thresholds. Mic input volume is 0..1 (from the SDK's
// getInputVolume / analyser fallback). While the agent is speaking the
// participant should be silent, so sustained mic input above NOISE_ON_LEVEL
// for NOISE_ON_SUSTAIN_MS signals a noisy room (e.g. a TV). Debounced both
// ways so the pill never flickers. These are starting values — calibrate on a
// real call (raise NOISE_ON_LEVEL if it false-fires, lower it if it misses).
const NOISE_ON_LEVEL = 0.12;        // mic level counted as "noise" while agent speaks
const NOISE_OFF_LEVEL = 0.06;       // mic level counted as "quiet again"
const NOISE_ON_SUSTAIN_MS = 2500;   // must stay noisy this long before showing the pill
const NOISE_OFF_SUSTAIN_MS = 2500;  // must stay quiet this long before hiding the pill

// Pre-compute gradient colors: #2E45DA (left) → #506ED3 (right) matching Figma
const BAR_COLORS = WAVE_BARS.map((_, i) => {
  const t = i / 79;
  const r = Math.round(61 + (120 - 61) * t);
  const g = Math.round(41 + (81 - 41) * t);
  const b = Math.round(105 + (207 - 105) * t);
  return `rgb(${r}, ${g}, ${b})`;
});

// AI assistant avatar icon (matches Figma — purple circle with robot face)
const AIAvatarIcon = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="24" fill="#2E45DA"/>
    <path d="M32 36H16C15.447 36 15 35.553 15 35C15 31.691 17.691 29 21 29H27C30.309 29 33 31.691 33 35C33 35.553 32.553 36 32 36ZM21.987 21.4H23.054L22.535 18.906L21.987 21.4ZM32.5 18H31.949C31.697 15.756 29.81 14 27.5 14H25V13C25 12.448 24.553 12 24 12C23.447 12 23 12.448 23 13V14H20.5C18.19 14 16.303 15.756 16.051 18H15.5C14.673 18 14 18.673 14 19.5V21.5C14 22.327 14.673 23 15.5 23H16.051C16.303 25.244 18.19 27 20.5 27H27.5C29.81 27 31.697 25.244 31.949 23H32.5C33.327 23 34 22.327 34 21.5V19.5C34 18.673 33.327 18 32.5 18ZM24.273 24C23.888 24.017 23.543 23.754 23.465 23.375L23.387 23H21.636L21.552 23.383C21.473 23.743 21.154 24 20.785 24C20.286 24 19.913 23.54 20.017 23.052L21.01 18.384C21.156 17.629 21.77 17.001 22.533 17.001H22.535C23.317 17.005 23.903 17.604 24.069 18.424L25.042 23.054C25.144 23.542 24.772 24 24.274 24H24.273ZM28 23.2C28 23.642 27.642 24 27.2 24C26.758 24 26.4 23.642 26.4 23.2V17.801C26.4 17.359 26.758 17.001 27.2 17.001C27.642 17.001 28 17.359 28 17.801V23.2Z" fill="white"/>
  </svg>
);

// Phone hangup icon (clean, like iPhone end call)
const PhoneOffIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 01-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" fill="currentColor"/>
  </svg>
);

// Filled mic-on icon
const MicOnIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 1a3.5 3.5 0 0 0-3.5 3.5v7a3.5 3.5 0 1 0 7 0v-7A3.5 3.5 0 0 0 12 1zm-1.5 3.5a1.5 1.5 0 1 1 3 0v7a1.5 1.5 0 1 1-3 0v-7z" fill="currentColor"/>
    <path d="M5.5 10a1 1 0 0 1 1 1v.5a5.5 5.5 0 1 0 11 0V11a1 1 0 1 1 2 0v.5a7.502 7.502 0 0 1-6.5 7.435V21h2.5a1 1 0 1 1 0 2h-7a1 1 0 1 1 0-2H11v-2.065A7.502 7.502 0 0 1 4.5 11.5V11a1 1 0 0 1 1-1z" fill="currentColor"/>
  </svg>
);

// Filled mic-off icon (matches Figma fi:mic-off)
const MicOffIcon = () => (
  <svg width="26" height="26" viewBox="0 0 26.25 26.25" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Mic body with cutoff */}
    <path d="M0.675049 8.83626C1.08373 8.66698 1.55426 8.76061 1.86707 9.07338L7.46719 14.6735C7.67227 14.8786 7.78764 15.1568 7.78763 15.4468C7.78754 15.737 7.67252 16.0161 7.46719 16.2212C6.85536 16.8323 6.07551 17.2479 5.22736 17.4164C4.37926 17.5849 3.50016 17.4983 2.70126 17.1676C1.90231 16.8367 1.21885 16.2767 0.738068 15.5579C0.257293 14.8392 0.000761624 13.9937 0 13.129V9.8467C4.06255e-05 9.40439 0.266433 9.00556 0.675049 8.83626ZM2.1875 13.1269L2.19391 13.2882C2.22198 13.6638 2.34664 14.0267 2.55707 14.3413C2.79746 14.7007 3.13922 14.9813 3.53867 15.1467C3.93798 15.3119 4.37732 15.3548 4.80118 15.2706C4.84729 15.2614 4.89266 15.2495 4.9379 15.2375L2.1875 12.4871V13.1269ZM6.5625 10.2184V4.37688C6.56303 3.83442 6.36212 3.31079 5.99854 2.90822C5.63497 2.50574 5.13466 2.25247 4.59503 2.19792C4.0553 2.1434 3.51401 2.29168 3.07724 2.61342C2.64071 2.93506 2.33931 3.40759 2.23129 3.93895C2.11111 4.53083 1.53396 4.91346 0.942078 4.79344C0.350118 4.67324 -0.0325805 4.09619 0.0875854 3.50423C0.303487 2.44097 0.907013 1.49558 1.78055 0.852097C2.65403 0.208774 3.73573 -0.0868521 4.81506 0.0221718C5.89437 0.131294 6.89495 0.637691 7.62207 1.44276C8.3488 2.24759 8.75079 3.29358 8.75 4.37795V10.2184C8.75 10.8225 8.26031 11.3121 7.65625 11.3121C7.05219 11.3121 6.5625 10.8225 6.5625 10.2184Z" fill="currentColor" transform="translate(8.75, 0)"/>
    {/* Curved base */}
    <path d="M0.00023048 1.09375C0.00023048 0.489757 0.490013 0.000110768 1.09398 0C1.69804 0 2.18773 0.489689 2.18773 1.09375V3.28873C2.17816 4.59638 2.55925 5.87787 3.28255 6.96732C4.00588 8.05675 5.03877 8.90499 6.24764 9.40369C7.45658 9.90237 8.78717 10.0284 10.0683 9.76578C11.3493 9.50312 12.523 8.86379 13.4382 7.92969C13.8609 7.49846 14.5534 7.49109 14.9848 7.91367C15.4162 8.33627 15.4232 9.02878 15.0008 9.4603C13.7806 10.7058 12.2154 11.5593 10.5073 11.9095C8.79922 12.2596 7.02527 12.0905 5.41344 11.4256C3.80166 10.7607 2.42477 9.63002 1.46034 8.17749C0.495955 6.725 -0.01242 5.01722 0.00023048 3.27377V1.09375ZM15.3127 1.09375C15.3127 0.489757 15.8025 0.000111038 16.4065 0C17.0105 0 17.5002 0.489689 17.5002 1.09375V3.28232C17.4998 3.79774 17.4534 4.31201 17.3624 4.81934C17.2558 5.41384 16.688 5.80921 16.0935 5.70267C15.499 5.59601 15.1037 5.02823 15.2102 4.43375C15.2784 4.05347 15.3123 3.66759 15.3127 3.28125V1.09375Z" fill="currentColor" transform="translate(4.375, 9.84)"/>
    {/* Bottom line */}
    <path d="M9.84375 0C10.4478 0 10.9375 0.489689 10.9375 1.09375C10.9375 1.69781 10.4478 2.1875 9.84375 2.1875H1.09375C0.489689 2.1875 0 1.69781 0 1.09375C0 0.489689 0.489689 0 1.09375 0H9.84375Z" fill="currentColor" transform="translate(7.656, 24.063)"/>
    {/* Vertical stem */}
    <path d="M0 5.46875V1.09375C0 0.489689 0.489689 0 1.09375 0C1.69781 0 2.1875 0.489689 2.1875 1.09375V5.46875C2.1875 6.07281 1.69781 6.5625 1.09375 6.5625C0.489689 6.5625 0 6.07281 0 5.46875Z" fill="currentColor" transform="translate(12.031, 19.688)"/>
    {/* Diagonal slash */}
    <path d="M0.320352 0.320352C0.747488 -0.106784 1.43985 -0.106784 1.86698 0.320352L25.9295 24.3829C26.3566 24.81 26.3566 25.5023 25.9295 25.9295C25.5023 26.3566 24.81 26.3566 24.3829 25.9295L0.320352 1.86698C-0.106784 1.43985 -0.106784 0.747488 0.320352 0.320352Z" fill="currentColor"/>
  </svg>
);

// PureSpectrum return trip: how often to check whether the outcome has been reported,
// and how long to keep the "Wrapping up" screen before telling the respondent they
// can close the page.
const PS_REDIRECT_POLL_MS = 15000;
const PS_WRAPUP_MAX_MS = 5 * 60 * 1000;
// Right after a web call ends, wait this long before asking whether the agent confirmed
// the PIN, so a PIN check made in the call's last seconds has landed.
const PS_CALL_END_CHECK_MS = 5000;

// "Ask for PIN" off web calls: report the call to our server (link at connect,
// started at the first answer). Retries a few times; never blocks the call.
const postWebCall = async (kind, body, attempt = 0, timeout = undefined) => {
  try {
    await axios.post(`${config.api.base_url}/survey-respondents/web-call/${kind}`, body, timeout ? { timeout } : undefined);
    return true;
  } catch (e) {
    if (attempt < 3) {
      await new Promise((r) => setTimeout(r, [1000, 3000, 9000][attempt]));
      return postWebCall(kind, body, attempt + 1, timeout);
    }
    return false;
  }
};
// The study's interviewer was changed after this page opened (Change Agent): the page
// reloads once to show the right interviewer, PIN and media. Remembered per interview for
// two minutes, so a second mismatch never reloads again (the call then uses the server's
// interviewer).
const AGENT_RELOAD_KEY = (tx) => `cv_agent_reload_${tx}`;
const reloadedForAgentRecently = (tx) => {
  try {
    const at = Number(window.sessionStorage.getItem(AGENT_RELOAD_KEY(tx)));
    return Number.isFinite(at) && at > 0 && Date.now() - at < 2 * 60 * 1000;
  } catch (e) { return true; } // no storage: never risk a reload loop
};
const markReloadedForAgent = (tx) => {
  try { window.sessionStorage.setItem(AGENT_RELOAD_KEY(tx), String(Date.now())); } catch (e) { /* ignore */ }
};

// Last-chance "started" report when the page is being closed (form-encoded: the
// server parses that; a beacon cannot carry JSON across origins).
const beaconWebCallStarted = (wc) => {
  try {
    // Only a Platform call where the person really answered and the report did not land yet.
    if (!wc || wc.startsAt !== 'answer' || !wc.answered || wc.reported || !navigator.sendBeacon) return;
    const body = new URLSearchParams({ ref: wc.ref, conversation_id: wc.conversationId || '' });
    navigator.sendBeacon(`${config.api.base_url}/survey-respondents/web-call/started`, body);
  } catch (e) { /* best effort */ }
};

const CampaignLandingPage = (props) => {
  const { slug, code } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Study "open link" (?ol=<token>): this browser's own interview record is fetched
  // first ('joining'), then the page runs exactly as for an invite link.
  const openLinkToken = searchParams.get('ol');
  const [openLinkJoin, setOpenLinkJoin] = useState(openLinkToken ? 'joining' : 'none'); // none | joining | done | refused
  const [openLinkRefusal, setOpenLinkRefusal] = useState(null);
  const [openLinkTx, setOpenLinkTx] = useState(null); // the record the join gave this browser
  const openLinkJoinStartedRef = useRef(false);
  const { getCampaignByLink, getCampaignByLinkReset, getCampaignByIdState, getRespondent, getRespondentState, getRespondentReset } = props;
  const [currentStep, setCurrentStep] = useState(1);
  const [pin, setPin] = useState("------");
  const [invalidAccess, setInvalidAccess] = useState(false);
  const [surveyClosed, setSurveyClosed] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [pinExpired, setPinExpired] = useState(false);
  // Start Call pressed and still getting ready (microphone, server check, connect).
  const [startingCall, setStartingCall] = useState(false);
  const startingCallRef = useRef(false);
  // No-PIN only: the link is closed for a reason other than the 1-hour limit.
  const [linkUnavailable, setLinkUnavailable] = useState(false);
  const [isNoisy, setIsNoisy] = useState(false);

  // Active call state
  const [micMuted, setMicMuted] = useState(false);
  const [callSeconds, setCallSeconds] = useState(0);
  const [showingMedia, setShowingMedia] = useState(null);
  const [mediaModalVisible, setMediaModalVisible] = useState(false);
  // The browser refused to start the media video with sound: it plays muted and a
  // "Tap for sound" button is shown until the respondent turns the sound on.
  const [soundBlocked, setSoundBlocked] = useState(false);
  // Bumped on every expand so the modal always gets a fresh <video> (a quick re-expand
  // would otherwise reuse the closing one, whose loadedmetadata never fires again).
  const [expandSeq, setExpandSeq] = useState(0);
  const [readyTimer, setReadyTimer] = useState(0);
  const [readyEnabled, setReadyEnabled] = useState(false);
  // PureSpectrum return trip (user decision 2026-10-03): once the call has ended the
  // respondent is sent back at once (as complete unless the outcome is already decided);
  // the finalizer reports the real decision server to server afterwards and corrects a
  // wrong complete through PS's reconciliation. Test links go back with the agent's mark.
  //   null     - nothing to show (call not over, or not a PS respondent)
  //   'checking'- a web call just ended; asking whether the agent confirmed the PIN
  //   'no_pin' - the call ended before the PIN was confirmed: nothing was recorded and the
  //              PIN stays valid until it expires, so the respondent may call again
  //   'waiting'- call over, decision pending ("Wrapping up")
  //   'timeout'- no decision within PS_WRAPUP_MAX_MS; PS already has (or will get) the
  //              status server to server, so the respondent may just close the page
  const [psWrapUp, setPsWrapUp] = useState(null);
  const [isPsRespondent, setIsPsRespondent] = useState(false);
  // Set only when this person's call was running while the study's agent was changed: a
  // reconnect must use the agent that started the call (backend sends call_agent then).
  const [callAgent, setCallAgent] = useState(null);
  const callEndedAtRef = useRef(null);
  // Refs so the ElevenLabs onDisconnect callback (bound once by useConversation) sees
  // current values.
  const isPsRespondentRef = useRef(false);
  const wasConnectedRef = useRef(false);
  // Web call on an "Ask for PIN" off interviewer: the ticket our server issued at Start
  // ({ ref, startsAt: 'connect' | 'answer', agentTurns, reported }). Null for PIN calls.
  const webCallRef = useRef(null);
  // The server said this interviewer asks for a PIN again (switched back on while this
  // page was open): show the PIN even though the loaded study said otherwise.
  const [pinModeForced, setPinModeForced] = useState(false);
  // The server handed this page a call ticket although the loaded study said the
  // interviewer asks for a PIN (switched off while the page was open).
  const [noPinForced, setNoPinForced] = useState(false);
  const mediaResolveRef = useRef(null);
  const mediaMapRef = useRef({});
  // Video media downloaded in the background once the call is connected, one at a time
  // in question order, so an ad plays at once (and again after expand) instead of
  // streaming a large file while the respondent waits. items: { media_id: { url, controller } }.
  const videoPreloadRef = useRef({ started: false, items: {} });
  // When the agent's current display_media call started: the "still viewing" answer
  // must go out within 120 s of it (the agent's hard limit for a tool answer).
  const toolCallAtRef = useRef(0);
  const readyTimerRef = useRef(null);
  const autoTimeoutRef = useRef(null);
  const videoDurationRef = useRef(0);
  // Media videos: the one on the call card and the one in the expand modal. Only one
  // of them plays at a time; expandFromRef carries the card video's position over.
  const inlineVideoRef = useRef(null);
  const modalVideoRef = useRef(null);
  const expandFromRef = useRef({ time: 0, wasPlaying: false, muted: false });
  // True from the moment the expand view is asked for (state lags a render behind).
  const mediaModalOpenRef = useRef(false);
  // The media video still waiting to start (it starts once the interviewer stops talking).
  const autoplayPendingRef = useRef(null);
  const showingMediaRef = useRef(null);
  const reCallSafetyRef = useRef(null);
  // { id, at } of the media the respondent finished during the auto-extend gap (false =
  // none): only the agent's prompt re-call of THAT media is answered "already viewed";
  // any other media, or a later request to see it again, is shown.
  const mediaDismissedRef = useRef(false);
  const timerRef = useRef(null);
  const waveformRef = useRef(null);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const micStreamRef = useRef(null);
  const animFrameRef = useRef(null);
  const isSpeakingRef = useRef(false);
  const micMutedRef = useRef(false);
  const conversationRef = useRef(null);
  const smoothedHeightsRef = useRef(WAVE_BARS.map(b => b.height));
  // Background-noise pill: current shown state + debounce timestamps (refs so
  // the per-frame animation loop reads/writes them without re-rendering).
  const isNoisyRef = useRef(false);
  const noiseAboveSinceRef = useRef(0);
  const noiseQuietSinceRef = useRef(0);

  const conversation = useConversation({
    micMuted,
    clientTools: {
      display_media: async (parameters) => {
        toolCallAtRef.current = Date.now();
        const mediaId = parameters.media_id;
        let media = mediaMapRef.current[mediaId];

        // Retry briefly if mediaMap not yet populated (race with campaign data load)
        if (!media && Object.keys(mediaMapRef.current).length === 0) {
          for (let i = 0; i < 5; i++) {
            await new Promise(r => setTimeout(r, 500));
            media = mediaMapRef.current[mediaId];
            if (media) break;
          }
        }

        if (!media || !media.url) {
          console.warn('[Media] Media not found for id:', mediaId);
          return "Media could not be displayed. Please continue with the question.";
        }

        // User already clicked "I'm Ready" during the auto-extend gap for this same media
        // and the agent re-calls it within the re-call window: don't re-show it. Any other
        // media (the next ad), or a later request to see it again, is shown as usual.
        if (mediaDismissedRef.current) {
          const dismissed = mediaDismissedRef.current;
          mediaDismissedRef.current = false;
          if (dismissed.id === media.mediaId && Date.now() - dismissed.at < 30000) {
            return "The participant has already finished viewing the media and is ready to respond. Ask for their response now.";
          }
        }

        // Auto-extend re-call: same media already showing — skip speech delay, just reset timer
        if (showingMediaRef.current && showingMediaRef.current.mediaId === media.mediaId) {
          // Clear the re-call safety timer — agent complied
          if (reCallSafetyRef.current) { clearTimeout(reCallSafetyRef.current); reCallSafetyRef.current = null; }
          // Reset the auto-timeout for another cycle
          if (autoTimeoutRef.current) clearTimeout(autoTimeoutRef.current);
          autoTimeoutRef.current = setTimeout(() => {
            if (mediaResolveRef.current) {
              const resolve = mediaResolveRef.current;
              mediaResolveRef.current = null;
              resolve("[MEDIA_STILL_VIEWING]");
              // Silent channel instructs agent to re-call display_media without speaking
              try { conversationRef.current?.sendContextualUpdate("Participant is still reviewing media. Do not speak. Call display_media again immediately with the same media_id."); } catch (e) {}
              // Safety: if agent doesn't re-call within 30s, dismiss media
              reCallSafetyRef.current = setTimeout(() => {
                if (showingMediaRef.current && !mediaResolveRef.current) {
                  setShowingMedia(null);
                  setMediaModalVisible(false);
                  setMicMuted(false);
                  if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
                  setReadyTimer(0);
                  setReadyEnabled(false);
                }
              }, 30000);
            }
          }, 100 * 1000);
          return new Promise((resolve) => {
            mediaResolveRef.current = resolve;
          });
        }

        // First display — resolve any existing pending media Promise (different media switching)
        if (mediaResolveRef.current) {
          mediaResolveRef.current("Switching to new media.");
          mediaResolveRef.current = null;
        }
        mediaDismissedRef.current = false;
        // Wait for agent to finish speaking the question before showing media
        await new Promise(r => setTimeout(r, 4000));

        // A video already downloaded in the background plays from memory.
        const url = playableUrl(media);
        setShowingMedia(url === media.url ? media : { ...media, url });
        setMediaModalVisible(false);
        setMicMuted(true);

        return new Promise((resolve) => {
          mediaResolveRef.current = resolve;
        });
      },
    },
    // "Ask for PIN" off: tell our server which call this is (it can't be matched by a
    // spoken PIN). PIN calls have no ticket and do nothing here, as before.
    onConnect: (props) => {
      const wc = webCallRef.current;
      const conversationId = (props && props.conversationId) || null;
      if (!wc || !conversationId) return;
      wc.conversationId = conversationId;
      postWebCall('link', { ref: wc.ref, conversation_id: conversationId });
    },
    onDisconnect: () => {
      // Clean up any pending media display and auto-extend state
      if (mediaResolveRef.current) {
        mediaResolveRef.current("Call ended");
        mediaResolveRef.current = null;
      }
      if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
      if (autoTimeoutRef.current) { clearTimeout(autoTimeoutRef.current); autoTimeoutRef.current = null; }
      if (reCallSafetyRef.current) { clearTimeout(reCallSafetyRef.current); reCallSafetyRef.current = null; }
      mediaDismissedRef.current = false;
      setShowingMedia(null);
      setMediaModalVisible(false);
      setMicMuted(false);
      // Fires for both ends: the respondent pressing End call and the agent hanging up.
      if (isPsRespondentRef.current) {
        if (!callEndedAtRef.current) callEndedAtRef.current = Date.now();
        setPsWrapUp((cur) => cur || 'checking');
      }
    },
    onError: (error) => {
      console.error('[ElevenLabs] Error:', error);
      notification.error({
        message: 'Call Error',
        description: error?.message || 'An error occurred during the call',
      });
    },
    // Platform studies on an "Ask for PIN" off interviewer: the interview counts as
    // started at the caller's first real answer (after the interviewer's 2nd line) -
    // the moment a spoken PIN was confirmed before. Nothing happens for PIN calls.
    onMessage: (payload) => {
      const wc = webCallRef.current;
      if (!wc || wc.startsAt !== 'answer' || wc.reported || !payload) return;
      const role = payload.role || (payload.source === 'ai' ? 'agent' : 'user');
      if (role === 'agent') { wc.agentTurns += 1; return; }
      const said = String(payload.message || '').replace(/[^\p{L}\p{N}]/gu, '');
      if (role === 'user' && wc.agentTurns >= 2 && said) wc.answered = true;
      if (wc.answered && !wc.reporting) {
        // Reported only once the server has it; until then every later answer retries.
        wc.reporting = true;
        postWebCall('started', { ref: wc.ref, conversation_id: wc.conversationId || null })
          .then((sent) => { if (sent) wc.reported = true; })
          .finally(() => { wc.reporting = false; });
      }
    },
  });

  // Keep ref for animation loop volume access
  conversationRef.current = conversation;

  // Track speaking/mute state in refs for animation loop
  useEffect(() => {
    isSpeakingRef.current = conversation.isSpeaking;
  }, [conversation.isSpeaking]);

  useEffect(() => {
    micMutedRef.current = micMuted;
  }, [micMuted]);

  // Sync showingMedia state to ref for closure access in display_media handler
  useEffect(() => {
    showingMediaRef.current = showingMedia;
  }, [showingMedia]);

  const agentId = callAgent?.agent_id ||
                  getCampaignByIdState.data?.data?.polling_agent_id?.agent_id ||
                  getCampaignByIdState.data?.polling_agent_id?.agent_id || null;
  // "Ask for PIN" off on this study's interviewer: no PIN is shown or asked; the page
  // gets a call ticket from our server at Start instead.
  const noPinAgent = !pinModeForced && (noPinForced || (getCampaignByIdState.data?.data?.polling_agent_id || getCampaignByIdState.data?.polling_agent_id)?.ask_pin === false);

  // If the page is closed before the first answer was reported, send it as a beacon.
  useEffect(() => {
    const onHide = () => beaconWebCallStarted(webCallRef.current);
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  // Build mediaMap when campaign data loads
  useEffect(() => {
    const campaignData = getCampaignByIdState.data?.data || getCampaignByIdState.data;
    const pq = callAgent?.prompt_questions || campaignData?.polling_agent_id?.prompt_questions;
    if (pq) {
      mediaMapRef.current = buildMediaMap(pq);
    }
  }, [getCampaignByIdState.data, callAgent]);

  // Download the study's videos in the background once the call is connected (the PIN
  // and intro questions come first), one at a time in question order, and not at all
  // when the browser asks to save data. Any failure leaves the normal streamed URL.
  // Page lifecycle of the downloads. Declared before the effect that starts them, and it
  // resets the state on every setup: React's development double-mount (StrictMode) runs
  // setup, cleanup, setup, and a download loop from an earlier setup stops by itself
  // (generation check).
  useEffect(() => {
    const state = videoPreloadRef.current;
    state.gen = (state.gen || 0) + 1;
    state.closed = false;
    state.started = false;
    state.streaming = null;
    state.items = {};
    return () => {
      state.closed = true;
      Object.values(state.items).forEach((item) => {
        item.controller.abort();
        if (item.url) URL.revokeObjectURL(item.url);
      });
    };
  }, []);

  const startVideoPreloads = useCallback(() => {
    const state = videoPreloadRef.current;
    if (state.started) return;
    if (typeof navigator !== 'undefined' && navigator.connection && navigator.connection.saveData) return;
    const videos = Object.values(mediaMapRef.current).filter((m) => m.type === 'video' && m.url);
    if (videos.length === 0) return;
    state.started = true;
    const gen = state.gen;
    const stopped = () => state.closed || state.gen !== gen;
    (async () => {
      for (const m of videos) {
        // While a video streams on screen it gets the whole connection.
        while (state.streaming && !stopped()) await new Promise((r) => setTimeout(r, 1000));
        if (stopped()) return;
        if (state.items[m.mediaId]) continue; // already shown (streaming) or downloaded
        const item = { url: null, controller: new AbortController() };
        state.items[m.mediaId] = item;
        try {
          const res = await fetch(m.url, { signal: item.controller.signal });
          const blob = res.ok ? await res.blob() : null;
          if (blob && !item.controller.signal.aborted && !stopped()) item.url = URL.createObjectURL(blob);
        } catch (_) { /* keep streaming the normal URL */ }
      }
    })();
  }, []);

  useEffect(() => {
    if (conversation.status === 'connected') startVideoPreloads();
  }, [conversation.status, getCampaignByIdState.data, startVideoPreloads]);

  // The URL to play a media with: the downloaded copy when it is ready. A download still
  // running is stopped (the video streams instead of being fetched twice), and a video
  // not reached yet is taken out of the queue.
  const playableUrl = useCallback((media) => {
    if (media.type !== 'video') return media.url;
    const state = videoPreloadRef.current;
    const item = state.items[media.mediaId];
    if (item && item.url) return item.url;
    if (item) item.controller.abort();
    else state.items[media.mediaId] = { url: null, controller: new AbortController() };
    state.streaming = media.mediaId;
    return media.url;
  }, []);

  // The queue waits only while that streamed video is on screen.
  useEffect(() => {
    const state = videoPreloadRef.current;
    if (state.streaming && (!showingMedia || showingMedia.mediaId !== state.streaming)) state.streaming = null;
  }, [showingMedia]);


  // Call timer
  useEffect(() => {
    if (conversation.status === 'connected') {
      timerRef.current = setInterval(() => {
        setCallSeconds(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (conversation.status === 'disconnected') {
        setCallSeconds(0);
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [conversation.status]);

  // Waveform animation with directional voice reactivity
  // Agent voice animates from left (darker) side, user mic from right (lighter) side
  useEffect(() => {
    if (conversation.status !== 'connected') {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
        micStreamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      smoothedHeightsRef.current = WAVE_BARS.map(b => b.height);
      // Clear the noise pill when the call is not connected.
      isNoisyRef.current = false;
      noiseAboveSinceRef.current = 0;
      noiseQuietSinceRef.current = 0;
      setIsNoisy(false);
      return;
    }

    // Start mic analyzer for user voice detection
    let mounted = true;
    const initAnalyzer = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (!mounted) {
          // Component unmounted while awaiting getUserMedia — clean up immediately
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        micStreamRef.current = stream;
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);
        analyserRef.current = analyser;
      } catch (e) {
        console.error('[Waveform] Mic analyzer init failed:', e);
      }
    };
    initAnalyzer();

    // Animation loop — agent animates from left, user mic animates from right
    const animate = () => {
      const container = waveformRef.current;
      if (!container) {
        animFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      const conv = conversationRef.current;
      const t = performance.now() / 1000;
      const bars = container.children;
      const smoothed = smoothedHeightsRef.current;

      // Get agent output volume from ElevenLabs SDK (real voice reactivity)
      let agentVol = 0;
      if (conv && typeof conv.getOutputVolume === 'function') {
        agentVol = conv.getOutputVolume();
      } else if (isSpeakingRef.current) {
        // Fallback: simulated volume when SDK method not available
        agentVol = Math.max(0, 0.4 + 0.2 * Math.sin(t * 3.1) + 0.15 * Math.sin(t * 7.3));
      }

      // Get user mic volume — prefer SDK, fallback to analyzer
      let userVol = 0;
      if (conv && typeof conv.getInputVolume === 'function') {
        userVol = conv.getInputVolume();
      } else {
        const analyser = analyserRef.current;
        if (analyser) {
          const freqData = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(freqData);
          let sum = 0;
          for (let j = 0; j < freqData.length; j++) sum += freqData[j];
          userVol = sum / (freqData.length * 255);
        }
      }

      // ── Background-noise pill ──────────────────────────────────────
      // While the agent is speaking the participant should be silent, so
      // sustained mic input during that window = ambient room noise (e.g. a
      // TV). Gated on agent-speaking so the participant's own answers are
      // never mistaken for noise. Debounced both directions (show + hide) so
      // the pill flashes in only on real, sustained noise and clears itself
      // once the room is quiet again.
      const nowMs = performance.now();
      if (isSpeakingRef.current && !micMutedRef.current && !showingMediaRef.current) {
        if (userVol > NOISE_ON_LEVEL) {
          noiseQuietSinceRef.current = 0;
          if (!noiseAboveSinceRef.current) noiseAboveSinceRef.current = nowMs;
          if (!isNoisyRef.current && nowMs - noiseAboveSinceRef.current > NOISE_ON_SUSTAIN_MS) {
            isNoisyRef.current = true;
            setIsNoisy(true);
          }
        } else if (userVol < NOISE_OFF_LEVEL) {
          noiseAboveSinceRef.current = 0;
          if (isNoisyRef.current) {
            if (!noiseQuietSinceRef.current) noiseQuietSinceRef.current = nowMs;
            if (nowMs - noiseQuietSinceRef.current > NOISE_OFF_SUSTAIN_MS) {
              isNoisyRef.current = false;
              setIsNoisy(false);
              noiseQuietSinceRef.current = 0;
            }
          }
        }
      }

      // Time seed for per-bar variation (changes ~8x/sec for organic wiggle)
      const tSlice = Math.floor(t * 8);

      for (let i = 0; i < WAVE_BARS.length && i < bars.length; i++) {
        const pos = i / 79; // 0 = left (AI/dark), 1 = right (user/light)
        const base = WAVE_BARS[i].height;

        // Agent influence — peaks on left, leaks slightly past center
        let agentH = 0;
        if (agentVol > 0.005) {
          const falloff = Math.pow(Math.max(0, 1 - pos * 1.5), 2);
          // Each bar wiggles independently (seeded random changes with time)
          const barVar = 0.3 + _sr(i * 7 + tSlice * 3) * 0.7;
          agentH = falloff * agentVol * barVar * 5.0;
        }

        // User mic — peaks on right, only when unmuted and above noise floor
        let userH = 0;
        if (userVol > 0.005 && !micMutedRef.current) {
          const falloff = Math.pow(Math.max(0, 1 - (1 - pos) * 1.5), 2);
          const barVar = 0.3 + _sr(i * 11 + tSlice * 5) * 0.7;
          userH = falloff * userVol * barVar * 5.0;
        }

        // Small bars idle, wiggle up proportional to voice volume
        const target = base + Math.max(agentH, userH);

        // Smooth interpolation
        smoothed[i] += (target - smoothed[i]) * 0.25;
        const finalH = Math.max(0.05, Math.min(0.95, smoothed[i]));

        bars[i].style.height = `${Math.round(finalH * 100)}%`;
      }

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      mounted = false;
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
        micStreamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
      analyserRef.current = null;
    };
  }, [conversation.status]);

  const handleStartCall = useCallback(async () => {
    if (!agentId) {
      notification.error({
        message: 'Cannot Start Call',
        description: 'No AI agent is configured for this study',
      });
      return;
    }
    if (startingCallRef.current) return;
    // Set once the microphone is allowed; only the press that set it clears it.
    let ownsStart = false;

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      // One Start at a time: the server check and the connect can take a few seconds,
      // and a second press must not start a second call. (Taken after the microphone
      // prompt: a prompt the person ignores must not leave the button stuck.)
      if (startingCallRef.current) return;
      startingCallRef.current = true;
      ownsStart = true;
      setStartingCall(true);
      webCallRef.current = null;
      const transactionId = searchParams.get('transaction_id');
      if (noPinAgent && !transactionId) {
        notification.error({ message: 'Cannot Start Call', description: 'Please open this interview from your invitation link.' });
        return;
      }
      // The interviewer our server named for this call (null = the page's own).
      let serverAgentId = null;
      if (transactionId) {
        // Our server decides the mode for this call (the interviewer's switch may have
        // changed since the page loaded). A PIN interviewer answers mode 'pin' and the
        // page runs today's flow; if the request itself fails on a PIN study, so does it.
        let ticket = {};
        try {
          const res = await axios.post(`${config.api.base_url}/survey-respondents/${encodeURIComponent(transactionId)}/web-call/start`, undefined, { timeout: 8000 });
          ticket = res.data?.data || {};
        } catch (e) {
          if (noPinAgent) throw e;
          ticket = { mode: 'pin' };
        }
        // The server just gave this person a new PIN (moved to the study's new interviewer
        // and their old PIN was taken there): reload so the page shows the PIN to say.
        // Happens at most once per person, so it cannot loop.
        if (ticket.pin_renewed) {
          markReloadedForAgent(transactionId);
          notification.info({ message: 'Your PIN was updated', description: 'Loading your new PIN...' });
          window.location.reload();
          return;
        }
        // A different interviewer than the one this page loaded: the study's agent was
        // changed after the page opened. Reload once so everything on the page matches.
        if (ticket.agent_id && ticket.agent_id !== agentId) {
          if (!reloadedForAgentRecently(transactionId)) {
            markReloadedForAgent(transactionId);
            notification.info({ message: 'This interview was just updated', description: 'Loading the latest version...' });
            window.location.reload();
            return;
          }
          serverAgentId = ticket.agent_id;
        }
        if (ticket.mode === 'ref' && !noPinAgent) setNoPinForced(true);
        if (ticket.mode === 'refused' && noPinAgent) {
          if (ticket.reason === 'completed') setAlreadyCompleted(true);
          else if (ticket.reason === 'expired') setPinExpired(true);
          else if (ticket.reason === 'not_available') { setLinkUnavailable(true); setPinExpired(true); }
          else notification.error({ message: 'Cannot Start Call', description: ticket.message || 'This interview is not available.' });
          return;
        }
        if (ticket.mode === 'ref') {
          webCallRef.current = { ref: ticket.ref, startsAt: ticket.starts_at, agentTurns: 0, answered: false, reported: false };
          await conversation.startSession({
            agentId: ticket.agent_id,
            // Phase 5: this person's block order, arm and pipe values travel with the call.
            dynamicVariables: { cv_call_ref: ticket.ref, ...(ticket.dynamic_variables || {}) },
            userId: ticket.respondent_id,
          });
          return;
        }
        if (ticket.mode === 'refused' && !noPinAgent) {
          // A PIN study that is already closed for this person: today's PIN flow decides.
        } else if (ticket.mode === 'pin' && noPinAgent) {
          // The interviewer was switched back to asking for a PIN - show the PIN and run
          // today's flow.
          setPinModeForced(true);
        }
      }
      await conversation.startSession({ agentId: serverAgentId || agentId });
    } catch (error) {
      console.error('[ElevenLabs] Failed to start call:', error);
      if (error.name === 'NotAllowedError') {
        notification.error({
          message: 'Microphone Access Required',
          description: 'Please allow microphone access to start the call',
        });
      } else {
        notification.error({
          message: 'Failed to Start Call',
          description: error?.message || 'Could not connect to the AI agent',
        });
      }
    } finally {
      if (ownsStart) {
        startingCallRef.current = false;
        setStartingCall(false);
      }
    }
  }, [agentId, conversation, noPinAgent, searchParams]);

  useEffect(() => { isPsRespondentRef.current = isPsRespondent; }, [isPsRespondent]);

  // A web call that just ended: check the PIN, then "Wrapping up" (phone calls are picked
  // up by the poll below from call_status, since this browser never sees them end). The
  // SDK ends a call as connected -> disconnecting -> disconnected, so this keys off "was
  // connected at some point", not the previous status. onDisconnect above covers the same
  // moment; whichever runs first wins.
  useEffect(() => {
    if (conversation.status === 'connected') {
      wasConnectedRef.current = true;
      return;
    }
    if (isPsRespondent && wasConnectedRef.current && (conversation.status === 'disconnecting' || conversation.status === 'disconnected')) {
      if (!callEndedAtRef.current) callEndedAtRef.current = Date.now();
      setPsWrapUp((cur) => cur || 'checking');
    }
  }, [conversation.status, isPsRespondent]);

  // Poll for the PS redirect. Runs for PS respondents from page load, so a respondent who
  // comes back after the decision is sent on at once, and a phone caller's page notices
  // the call ended. Stops on redirect or after the wrap-up cap.
  useEffect(() => {
    const transactionId = searchParams.get('transaction_id');
    if (!transactionId || !isPsRespondent || psWrapUp === 'timeout') return undefined;
    let cancelled = false;
    const check = async () => {
      try {
        // ended=1: this page saw its web call end, so the backend may send the respondent
        // back now instead of waiting for the call to be closed on our side.
        const ended = psWrapUp === 'checking' || psWrapUp === 'waiting';
        const res = await axios.get(`${config.api.base_url}/survey-respondents/${encodeURIComponent(transactionId)}/ps-redirect${ended ? '?ended=1' : ''}`);
        const d = res.data?.data || {};
        if (cancelled) return;
        // Never pull the respondent out of a live web call (e.g. their PIN expired just as
        // they started one): the check after the call ends picks the redirect up.
        const callLive = ['connected', 'connecting'].includes(conversationRef.current?.status);
        if (d.ready && d.redirect_url && !callLive) {
          cancelled = true;
          window.location.href = d.redirect_url;
          return;
        }
        if (d.reason === 'not_purespectrum' || d.reason === 'not_found') {
          callEndedAtRef.current = null;
          setPsWrapUp(null);
          setIsPsRespondent(false);
          return;
        }
        if (psWrapUp === 'checking' && !callLive) {
          // 'pending' = the agent never confirmed the PIN (refused, wrong PIN, hung up
          // early): nothing was recorded, so no wrap-up and no 5-minute cap; the respondent
          // may call again until the PIN expires (then the redirect above sends them back).
          if (d.call_status === 'pending') {
            callEndedAtRef.current = null;
            setPsWrapUp('no_pin');
            return;
          }
          setPsWrapUp('waiting');
        }
        if (['completed', 'incomplete'].includes(d.call_status) && !callLive) {
          if (!callEndedAtRef.current) callEndedAtRef.current = Date.now();
          setPsWrapUp((cur) => (!cur || cur === 'no_pin' ? 'waiting' : cur));
        }
        if (psWrapUp === 'waiting' && callEndedAtRef.current && Date.now() - callEndedAtRef.current > PS_WRAPUP_MAX_MS) {
          setPsWrapUp('timeout');
        }
      } catch (e) {
        // Network blip: try again on the next tick.
      }
    };
    const firstCheck = setTimeout(check, psWrapUp === 'checking' ? PS_CALL_END_CHECK_MS : 0);
    const id = setInterval(check, PS_REDIRECT_POLL_MS);
    return () => { cancelled = true; clearTimeout(firstCheck); clearInterval(id); };
  }, [searchParams, isPsRespondent, psWrapUp]);

  // Phase 5: on a questionnaire call the person can move on while the agent is
  // still reading options; the agent hears it as a request for the next question.
  const campaignForSkip = getCampaignByIdState.data?.data || getCampaignByIdState.data;
  const canSkipAhead = !!campaignForSkip && (campaignForSkip.modality === 'voice_quant' || campaignForSkip.modality === 'respondent_choice' || !!campaignForSkip.survey_definition_id);
  const [skipCooldown, setSkipCooldown] = useState(false);
  const handleSkipAhead = useCallback(() => {
    if (skipCooldown || conversation.status !== 'connected') return;
    try {
      if (typeof conversation.sendUserActivity === 'function') conversation.sendUserActivity();
      if (typeof conversation.sendUserMessage === 'function') conversation.sendUserMessage('I have my answer, please go on to the next question.');
    } catch (e) {
      console.error('[ElevenLabs] skip ahead failed:', e);
    }
    setSkipCooldown(true);
    setTimeout(() => setSkipCooldown(false), 4000);
  }, [conversation, skipCooldown]);

  const handleEndCall = useCallback(async () => {
    // Read the call's id before hanging up (the SDK drops it with the session).
    const conversationId = typeof conversation.getId === 'function' ? conversation.getId() : null;
    try {
      await conversation.endSession();
    } catch (error) {
      console.error('[ElevenLabs] Failed to end call:', error);
    }
    // "Ask for PIN" off, Platform study: the person answered but the "started" report has
    // not landed yet. Send it first (one try, short wait), so the close below finds the
    // interview started and the page never says it ended before it began.
    const wc = webCallRef.current;
    if (wc && wc.startsAt === 'answer' && wc.answered && !wc.reported) {
      const sent = await postWebCall('started', { ref: wc.ref, conversation_id: wc.conversationId || conversationId || null }, 3, 4000);
      if (sent) wc.reported = true;
    }
    // Tell our server right away that the respondent ended the call, so the record is
    // closed (finalizing) now instead of when ElevenLabs reports it a minute later: a
    // reload then sends a PS respondent straight back. Best effort; the post-call message
    // still closes it if this request is lost.
    const transactionId = searchParams.get('transaction_id');
    if (transactionId && conversationId) {
      axios.post(`${config.api.base_url}/survey-respondents/${encodeURIComponent(transactionId)}/call-ended`, { conversation_id: conversationId })
        .catch(() => {});
    }
  }, [conversation, searchParams]);

  // Start a media video with sound. Browsers allow that once the respondent has clicked
  // on the page (Start call); if one still refuses, the video plays muted and the
  // "Tap for sound" button turns the sound on with the respondent's click.
  const playWithSound = useCallback((el) => {
    if (!el) return;
    el.muted = false;
    const started = el.play();
    if (!started || typeof started.then !== 'function') return;
    started.then(() => setSoundBlocked(false)).catch((err) => {
      if (!err || err.name !== 'NotAllowedError' || !el.isConnected) return;
      el.muted = true;
      setSoundBlocked(true);
      el.play().catch(() => {});
    });
  }, []);

  // Carry a video on in the other view: a mute the respondent chose stays on, otherwise
  // it plays with sound (or the "Tap for sound" fallback).
  const resumeVideo = useCallback((el, keepMuted) => {
    if (!el) return;
    if (keepMuted) { el.muted = true; el.play().catch(() => {}); } else playWithSound(el);
  }, [playWithSound]);

  useEffect(() => {
    mediaModalOpenRef.current = mediaModalVisible;
  }, [mediaModalVisible]);

  // A new media video starts playing, with sound, once the interviewer has stopped
  // talking (the end of the question can still be playing when the media appears),
  // and at the latest 10 s after it is shown.
  useEffect(() => {
    setSoundBlocked(false);
    autoplayPendingRef.current = showingMedia?.type === 'video' ? showingMedia : null;
  }, [showingMedia]);

  useEffect(() => {
    const pending = autoplayPendingRef.current;
    if (!pending || pending !== showingMedia) return undefined;
    const start = () => {
      if (autoplayPendingRef.current !== pending) return;
      autoplayPendingRef.current = null;
      playWithSound(mediaModalOpenRef.current ? modalVideoRef.current : inlineVideoRef.current);
    };
    if (!conversation.isSpeaking) { start(); return undefined; }
    const timer = setTimeout(start, 10000);
    return () => clearTimeout(timer);
  }, [showingMedia, conversation.isSpeaking, playWithSound]);

  const handleSoundOn = useCallback(() => {
    const el = mediaModalVisible ? modalVideoRef.current : inlineVideoRef.current;
    if (!el) return;
    el.muted = false;
    if (el.volume === 0) el.volume = 1;
    setSoundBlocked(false);
    if (el.paused && !el.ended) el.play().catch(() => {});
  }, [mediaModalVisible]);

  // Expand: the card video pauses and the modal one continues from the same moment.
  const handleExpandMedia = useCallback(() => {
    if (mediaModalOpenRef.current) return; // Safari reports a native fullscreen twice
    mediaModalOpenRef.current = true;
    const el = inlineVideoRef.current;
    if (el && showingMedia?.type === 'video') {
      expandFromRef.current = {
        time: el.ended ? 0 : (el.currentTime || 0),
        wasPlaying: !el.paused && !el.ended,
        muted: el.muted && !soundBlocked, // muted by the respondent, not by the browser
      };
      el.pause();
      setSoundBlocked(false); // the modal video starts unmuted; a refusal sets it again
    }
    setExpandSeq((n) => n + 1);
    setMediaModalVisible(true);
  }, [showingMedia, soundBlocked]);

  // Close: the modal video stops and the card video carries on from where it was.
  const handleCloseMediaModal = useCallback(() => {
    const modalEl = modalVideoRef.current;
    const el = inlineVideoRef.current;
    if (el && showingMedia?.type === 'video') {
      let time = expandFromRef.current.time;
      let resume = expandFromRef.current.wasPlaying;
      let keepMuted = expandFromRef.current.muted;
      if (modalEl && modalEl.readyState >= 1) {
        time = modalEl.currentTime || 0;
        resume = !modalEl.paused && !modalEl.ended;
        if (modalEl.ended) time = el.duration || time;
        keepMuted = modalEl.muted && !soundBlocked;
      }
      if (modalEl) {
        modalEl.pause();
        el.muted = modalEl.muted;
        if (!modalEl.muted) setSoundBlocked(false);
      }
      try { el.currentTime = time; } catch (_) { /* metadata not loaded yet */ }
      if (resume) resumeVideo(el, keepMuted);
    }
    mediaModalOpenRef.current = false;
    setMediaModalVisible(false);
  }, [showingMedia, soundBlocked, resumeVideo]);

  // The expand view is the only full view of a media video. Browsers that still offer
  // their own fullscreen (double-click, Firefox / Safari ignore controlsList, iPhone)
  // are taken straight back out of it; from the card video that opens the expand view.
  useEffect(() => {
    if (showingMedia?.type !== 'video') return undefined;
    const isMediaVideo = (el) => !!el && (el === inlineVideoRef.current || el === modalVideoRef.current);
    const leaveNativeFullscreen = (el) => {
      if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
      else if (document.webkitFullscreenElement && document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (el && el.webkitDisplayingFullscreen && el.webkitExitFullscreen) el.webkitExitFullscreen();
      if (el === inlineVideoRef.current) handleExpandMedia();
    };
    const onDocFullscreen = () => {
      const el = document.fullscreenElement || document.webkitFullscreenElement;
      if (isMediaVideo(el)) leaveNativeFullscreen(el);
    };
    const onVideoFullscreen = (e) => leaveNativeFullscreen(e.target); // iPhone Safari
    const videos = [inlineVideoRef.current, modalVideoRef.current].filter(Boolean);
    document.addEventListener('fullscreenchange', onDocFullscreen);
    document.addEventListener('webkitfullscreenchange', onDocFullscreen);
    videos.forEach((v) => v.addEventListener('webkitbeginfullscreen', onVideoFullscreen));
    return () => {
      document.removeEventListener('fullscreenchange', onDocFullscreen);
      document.removeEventListener('webkitfullscreenchange', onDocFullscreen);
      videos.forEach((v) => v.removeEventListener('webkitbeginfullscreen', onVideoFullscreen));
    };
  }, [showingMedia, mediaModalVisible, handleExpandMedia]);

  // Esc closes the expand view even when focus is outside it (e.g. after a browser
  // fullscreen was undone). With focus inside, the modal handles Esc itself and stops it.
  useEffect(() => {
    if (!mediaModalVisible) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') handleCloseMediaModal(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mediaModalVisible, handleCloseMediaModal]);

  const handleToggleMute = useCallback(() => {
    if (showingMedia) return; // Disabled during media display
    setMicMuted(prev => !prev);
  }, [showingMedia]);

  // Countdown timer for "I'm Ready" button based on media type
  const startReadyCountdown = useCallback((mediaType) => {
    if (readyTimerRef.current) clearInterval(readyTimerRef.current);
    setReadyEnabled(false);
    // Base delay: image=5s, pdf/link=15s, video=handled by onLoadedMetadata
    let delay = mediaType === 'image' ? 5 : (mediaType === 'pdf' || mediaType === 'link') ? 15 : 10;
    if (mediaType === 'video' && videoDurationRef.current > 0) {
      delay = Math.min(Math.ceil(videoDurationRef.current), 30);
    }
    setReadyTimer(delay);
    readyTimerRef.current = setInterval(() => {
      setReadyTimer(prev => {
        if (prev <= 1) {
          clearInterval(readyTimerRef.current);
          readyTimerRef.current = null;
          setReadyEnabled(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Auto-timeout: resolves 100 s after the agent's display_media call (20 s buffer before
  // the agent's 120 s hard limit). Counted from the call, once per call: a video whose
  // details load late must not push it past the limit (the agent then gives up, says the
  // media "isn't loading" and the media is shown again from the start).
  // Instead of dismissing media, triggers auto-extend loop - tells agent to re-call display_media
  const startAutoTimeout = useCallback(() => {
    if (autoTimeoutRef.current) return; // already running for this call
    const elapsed = toolCallAtRef.current ? Date.now() - toolCallAtRef.current : 0;
    autoTimeoutRef.current = setTimeout(() => {
      autoTimeoutRef.current = null;
      if (mediaResolveRef.current) {
        // Auto-extend: resolve with re-call instruction, keep media on screen
        const resolve = mediaResolveRef.current;
        mediaResolveRef.current = null;
        resolve("[MEDIA_STILL_VIEWING]");
        // Silent channel instructs agent to re-call display_media without speaking
        try { conversationRef.current?.sendContextualUpdate("Participant is still reviewing media. Do not speak. Call display_media again immediately with the same media_id."); } catch (e) {}
        // Safety: if agent doesn't re-call within 30s, dismiss media
        if (reCallSafetyRef.current) clearTimeout(reCallSafetyRef.current);
        reCallSafetyRef.current = setTimeout(() => {
          if (showingMediaRef.current && !mediaResolveRef.current) {
            setShowingMedia(null);
            setMediaModalVisible(false);
            setMicMuted(false);
            if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
            setReadyTimer(0);
            setReadyEnabled(false);
          }
        }, 30000);
      }
    }, Math.max(1000, 100 * 1000 - elapsed));
  }, []);

  // Start countdown when media is shown
  useEffect(() => {
    let videoFallbackTimer;
    if (showingMedia) {
      videoDurationRef.current = 0;
      if (showingMedia.type !== 'video') {
        startReadyCountdown(showingMedia.type);
        startAutoTimeout();
      } else {
        // Fallback: if video onLoadedMetadata doesn't fire within 5s, use default 10s countdown
        videoFallbackTimer = setTimeout(() => {
          if (videoDurationRef.current === 0) {
            startReadyCountdown('video');
            startAutoTimeout();
          }
        }, 5000);
      }
    } else {
      if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
      if (autoTimeoutRef.current) { clearTimeout(autoTimeoutRef.current); autoTimeoutRef.current = null; }
      if (reCallSafetyRef.current) { clearTimeout(reCallSafetyRef.current); reCallSafetyRef.current = null; }
      setReadyTimer(0);
      setReadyEnabled(false);
    }
    return () => {
      if (videoFallbackTimer) clearTimeout(videoFallbackTimer);
      if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
      if (autoTimeoutRef.current) { clearTimeout(autoTimeoutRef.current); autoTimeoutRef.current = null; }
      if (reCallSafetyRef.current) { clearTimeout(reCallSafetyRef.current); reCallSafetyRef.current = null; }
    };
  }, [showingMedia, startReadyCountdown, startAutoTimeout]);

  // Safety: if agent starts speaking while media is stuck (no pending Promise = tool timed out),
  // auto-dismiss the media so the participant can respond
  useEffect(() => {
    if (conversation.isSpeaking && showingMedia && !mediaResolveRef.current) {
      // Agent moved on but media is stuck — clean up
      setShowingMedia(null);
      setMediaModalVisible(false);
      setMicMuted(false);
      if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
      if (autoTimeoutRef.current) { clearTimeout(autoTimeoutRef.current); autoTimeoutRef.current = null; }
      if (reCallSafetyRef.current) { clearTimeout(reCallSafetyRef.current); reCallSafetyRef.current = null; }
      setReadyTimer(0);
      setReadyEnabled(false);
    }
  }, [conversation.isSpeaking, showingMedia]);

  const handleReady = useCallback(() => {
    setShowingMedia(null);
    setMediaModalVisible(false);
    setMicMuted(false);
    if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
    if (autoTimeoutRef.current) { clearTimeout(autoTimeoutRef.current); autoTimeoutRef.current = null; }
    if (reCallSafetyRef.current) { clearTimeout(reCallSafetyRef.current); reCallSafetyRef.current = null; }
    setReadyTimer(0);
    setReadyEnabled(false);
    // Only in the auto-extend gap (the tool already answered "still viewing", so nothing
    // is pending) will the agent re-call this media: remember which one, so that re-call
    // is not shown again. A normal "ready" answers the pending call and needs no flag.
    const gapMediaId = !mediaResolveRef.current && showingMediaRef.current?.mediaId;
    mediaDismissedRef.current = gapMediaId ? { id: gapMediaId, at: Date.now() } : false;
    if (mediaResolveRef.current) {
      mediaResolveRef.current("User has reviewed the media and is ready. Now ask for their response to the question.");
      mediaResolveRef.current = null;
    }
  }, []);

  const getCallStatusText = () => {
    switch (conversation.status) {
      case 'connecting': return 'Connecting...';
      case 'connected': return 'Call in progress';
      case 'disconnecting': return 'Ending call...';
      default: return 'Ready to call';
    }
  };

  // Fetch campaign data
  useEffect(() => {
    if (slug && code) {
      getCampaignByLink(slug, code);
    }
  }, [slug, code, getCampaignByLink]);

  // Handle campaign data response
  useEffect(() => {
    if (getCampaignByIdState.apiState === 'success') {
      const campaignData = getCampaignByIdState.data?.data || getCampaignByIdState.data;
      if (campaignData?.status === 'completed' || campaignData?.status === 'closed') {
        setSurveyClosed(true);
      }
    } else if (getCampaignByIdState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getCampaignByIdState.message || 'Study not found',
      });
      getCampaignByLinkReset();
    }
  }, [getCampaignByIdState, getCampaignByLinkReset]);

  // Open link: ask our server for this browser's own record (the same one until this
  // person has taken a call), then put its transaction id in the address so the page
  // continues exactly like an invite link (PIN shown, or the no-PIN call ticket).
  useEffect(() => {
    if (!openLinkToken || openLinkJoinStartedRef.current) return;
    openLinkJoinStartedRef.current = true;
    const storageKey = `cv_open_link_${slug}_${code}`;
    let known = null;
    try { known = window.localStorage.getItem(storageKey); } catch (e) { known = null; }
    axios.post(`${config.api.base_url}/survey-respondents/open-link/join`, { token: openLinkToken, slug, code, transaction_id: known || undefined }, { timeout: 20000 })
      .then((res) => {
        const tx = res.data?.data?.transaction_id;
        if (!tx) {
          // Refused (turned off, expired, invalid, study closed): the server says why.
          setOpenLinkRefusal({ reason: res.data?.data?.reason || 'error', message: res.data?.message || 'This interview is not available.' });
          setOpenLinkJoin('refused');
          return;
        }
        try { window.localStorage.setItem(storageKey, tx); } catch (e) { /* private mode: a new record next time */ }
        setOpenLinkTx(tx);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('transaction_id', tx);
          next.delete('sid');
          return next;
        }, { replace: true });
        setOpenLinkJoin('done');
      })
      .catch((err) => {
        const body = err?.response?.data || {};
        const reason = body.data?.reason || (err?.response?.status === 429 ? 'busy' : 'error');
        setOpenLinkRefusal({ reason, message: body.message || 'We could not open this interview. Please check your connection and try again.' });
        setOpenLinkJoin('refused');
      });
  }, [openLinkToken, slug, code, setSearchParams]);

  // Get existing respondent PIN by transaction_id (sid = per-attempt send id from the invite
  // link, forwarded so the backend can attribute the landing to the exact email attempt)
  useEffect(() => {
    // Open link: wait for this browser's own record (never a transaction id carried in a
    // forwarded address).
    if (openLinkToken && (openLinkJoin !== 'done' || searchParams.get('transaction_id') !== openLinkTx)) return;
    const transaction_id = searchParams.get('transaction_id');
    const sid = searchParams.get('sid');
    if (transaction_id) {
      getRespondent(transaction_id, sid).catch((error) => {
        console.error('Error fetching respondent:', error);
      });
    }
  }, [searchParams, getRespondent, openLinkToken, openLinkJoin, openLinkTx]);

  // Handle respondent data response
  useEffect(() => {
    if (getRespondentState.apiState === 'success' && getRespondentState.data) {
      const responseData = getRespondentState.data;
      const respondentData = responseData.data || responseData;
      const pinValue = respondentData.pin;

      if (pinValue) {
        setPin(pinValue);
        setInvalidAccess(false);
      }
      setIsPsRespondent(respondentData.invite_source !== 'email_invite');
      setCallAgent(respondentData.call_agent && respondentData.call_agent.agent_id ? respondentData.call_agent : null);

      if (respondentData.call_status === 'completed') {
        setAlreadyCompleted(true);
      } else if (
        respondentData.invite_source === 'purespectrum' &&
        respondentData.survey_status === 'abandoned' &&
        !respondentData.call_started_at
      ) {
        setPinExpired(true);
      }

      getRespondentReset();
    } else if (getRespondentState.apiState === 'error') {
      console.error('Failed to fetch respondent:', getRespondentState.message);
      const transaction_id = searchParams.get('transaction_id');
      if (transaction_id) {
        setInvalidAccess(true);
      }
      setPin('------');
      getRespondentReset();
    }
  }, [getRespondentState, getRespondentReset, searchParams]);

  // Parse campaign data
  const responseData = getCampaignByIdState.data;
  const campaign = responseData?.data || responseData || null;

  // A study with no Study Message has nothing to show on the intro step, so the participant is
  // taken straight to the PIN / Start Call screen - one less tap between the invite and the
  // interview. Studies that DO have a message keep the intro step exactly as before.
  // Same emptiness test the study forms use: the message is rich text, so "<p></p>" counts as empty.
  // "Ask for PIN" off: the welcome text must not send people looking for a PIN or a
  // phone number (the default study message does). Paragraphs that mention either
  // are dropped for the display only; the stored message is untouched.
  const introHtml = noPinAgent
    ? (campaign?.survey_message || '').replace(/<p\b[^>]*>[\s\S]*?<\/p>/gi, (p) => (/\bPIN\b|phone number/i.test(p.replace(/<[^>]*>/g, '')) ? '' : p))
    : (campaign?.survey_message || '');
  const hasIntroMessage = !!introHtml
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .trim();
  // Derived during render, NOT set from an effect: an effect runs after paint, so the participant
  // would see one frame of the empty intro card before it jumped to the PIN screen.
  const effectiveStep = hasIntroMessage ? currentStep : 2;

  const handleNext = () => {
    setCurrentStep(2);
  };

  const phoneNumbers = (callAgent ? callAgent.phone_numbers : campaign?.polling_agent_id?.phone_numbers) || [];
  const studyName = campaign?.topic || campaign?.campaign_name || "Survey";

  const hasTransactionId = searchParams.get('transaction_id');
  const isRespondentLoading = hasTransactionId && getRespondentState.apiState === 'loading';

  const isCallActive = conversation.status === 'connected';

  // ─── Loading / Error / Status screens ────────────────────────────

  // Open link: keep "Loading" until the address carries this browser's own record.
  const openLinkPending = !!openLinkToken && (openLinkJoin === 'joining' || (openLinkJoin === 'done' && searchParams.get('transaction_id') !== openLinkTx));
  if (getCampaignByIdState.apiState === 'loading' || isRespondentLoading || openLinkPending) {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              {/* inline: without it LottieLoader renders as a fixed full-screen overlay that covers
                  the "Processing... / Loading survey..." text sitting right below it in this card. */}
              <LottieLoader inline size="medium" />
              <Title style={{ marginTop: '24px', marginBottom: '8px' }}>Processing...</Title>
              <MessageText style={{ marginBottom: 0 }}>Loading survey...</MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  if (!campaign) {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Title style={{ marginBottom: '16px' }}>Study Not Found</Title>
              <MessageText style={{ color: '#666', lineHeight: '1.6', marginBottom: 0 }}>
                The study you're looking for doesn't exist or has been removed.
              </MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  if (openLinkJoin === 'refused' && !surveyClosed) {
    const titles = { off: 'Link Turned Off', expired: 'Link Expired', invalid: 'Invalid Link', study_closed: 'Survey Closed', busy: 'Please Try Again', error: 'Please Try Again' };
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Title style={{ marginBottom: '16px' }}>{titles[openLinkRefusal?.reason] || 'Interview Not Available'}</Title>
              <MessageText style={{ color: '#666', lineHeight: '1.6', marginBottom: 0 }}>
                {openLinkRefusal?.message || 'This interview is not available.'}
              </MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  if (surveyClosed) {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Title style={{ marginBottom: '16px' }}>Survey Closed</Title>
              <MessageText style={{ color: '#666', lineHeight: '1.6' }}>
                Uh oh! You missed it. This survey has been closed and is no longer accepting responses.
                <br /><br />
                Thank you for your interest in participating.
              </MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  if (psWrapUp === 'checking' && !isCallActive) {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <LottieLoader inline size="medium" />
              <Title style={{ marginTop: '24px', marginBottom: 0 }}>Ending call...</Title>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  // Reloading is the retry: the page reads the record again and shows the Start Call screen
  // with the same PIN, or "PIN Expired" / "Already Completed" / "Survey Closed" if that is
  // where the respondent now stands.
  if (psWrapUp === 'no_pin' && !isCallActive) {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Title style={{ marginBottom: '16px' }}>Your Call Ended</Title>
              <MessageText style={{ color: '#666', lineHeight: '1.6' }}>
                {noPinAgent
                  ? 'Your call ended before the interview started. You can start the call again.'
                  : 'Your call ended before your PIN was confirmed. You can start the call again.'}
              </MessageText>
              <WebCallButton
                className="start-call"
                onClick={() => window.location.reload()}
                icon={<AudioOutlined />}
              >
                Try Again
              </WebCallButton>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  if (psWrapUp && !isCallActive) {
    const timedOut = psWrapUp === 'timeout';
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              {timedOut
                ? <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div>
                : <LottieLoader inline size="medium" />}
              <Title style={{ marginTop: timedOut ? 0 : '24px', marginBottom: '16px' }}>
                {timedOut ? 'Thank You' : 'Wrapping Up Your Interview'}
              </Title>
              <MessageText style={{ color: '#666', lineHeight: '1.6', marginBottom: 0 }}>
                {timedOut
                  ? 'Your interview has been recorded. You can close this page now.'
                  : 'This usually takes a minute or two. Please keep this page open and you will be taken back automatically.'}
              </MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  if (alreadyCompleted) {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div>
              <Title style={{ marginBottom: '16px' }}>Already Completed</Title>
              <MessageText style={{ color: '#666', lineHeight: '1.6', marginBottom: 0 }}>
                You have already completed this survey. Thank you for your participation!
              </MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  if (pinExpired) {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#128337;</div>
              <Title style={{ marginBottom: '16px' }}>{noPinAgent ? (linkUnavailable ? 'Interview Not Available' : 'Link Expired') : 'PIN Expired'}</Title>
              <MessageText style={{ color: '#666', lineHeight: '1.6', marginBottom: 0 }}>
                {noPinAgent
                  ? (linkUnavailable
                    ? 'Sorry, this interview is no longer available.'
                    : 'Uh oh! This interview link has expired. Interview links are valid for 1 hour after being opened.')
                  : 'Uh oh! Your PIN has expired. Survey PINs are valid for 1 hour after being issued.'}
                <br /><br />
                Please contact support if you believe this is an error.
              </MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  if (invalidAccess && campaign?.study_type !== 'email-invite') {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Title style={{ marginBottom: '16px' }}>Invalid Link</Title>
              <MessageText style={{ color: '#666', lineHeight: '1.6' }}>
                Uh oh! Looks like this survey link is no longer valid.
                <br /><br />
                The link may have expired or been removed. Please contact support if you believe this is an error.
              </MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  // ─── Main render ─────────────────────────────────────────────────

  return (
    <LandingPageContainer>
      <MediaPreviewModalStyle />
      <BackgroundContainer />
      <LandingPageWrapper>
        <Logo>
          <img src="/images/logo.svg" alt="CrowdVox" />
        </Logo>

        {isCallActive ? (
          // ── Active Call UI ──
          <ActiveCallCard $withMedia={!!showingMedia}>
            <CallHeader>
              <CallAvatar>
                <AIAvatarIcon size={48} />
              </CallAvatar>
              <CallHeaderInfo>
                <CallStudyName>{studyName}</CallStudyName>
              </CallHeaderInfo>
              {!noPinAgent && (
                <CallPinBadge>
                  <span>PIN:</span>
                  <span className="pin-digits">{pin}</span>
                </CallPinBadge>
              )}
              <CallHeaderRight>
                <LiveBadge>
                  <span className="live-dot" />
                  <span className="live-text">Live Call</span>
                </LiveBadge>
              </CallHeaderRight>
            </CallHeader>

            <CallContent>
              {!showingMedia && <TimerDisplay>{formatTimer(callSeconds)}</TimerDisplay>}
              {showingMedia ? (
                <>
                  <MediaDisplayArea $wide={showingMedia.type === 'pdf'}>
                    {showingMedia.type === 'image' && (
                      <img src={showingMedia.url} alt={showingMedia.filename || 'Media'} />
                    )}
                    {showingMedia.type === 'video' && (
                      <video
                        ref={inlineVideoRef}
                        src={showingMedia.url}
                        playsInline
                        controls
                        controlsList="nodownload nofullscreen"
                        disablePictureInPicture
                        onPlay={(e) => { autoplayPendingRef.current = null; if (!e.target.muted) setSoundBlocked(false); }}
                        onVolumeChange={(e) => { if (!e.target.muted && e.target.volume > 0) setSoundBlocked(false); }}
                        onLoadedMetadata={(e) => {
                          const dur = Math.ceil(e.target.duration || 10);
                          videoDurationRef.current = dur;
                          startReadyCountdown('video');
                          startAutoTimeout();
                        }}
                      />
                    )}
                    {showingMedia.type === 'pdf' && (
                      <iframe
                        src={`${showingMedia.url}#toolbar=0&navpanes=0&view=FitH`}
                        title={showingMedia.filename || 'Document'}
                      />
                    )}
                    {showingMedia.type === 'link' && !mediaModalVisible && (
                      <iframe
                        src={`${showingMedia.url}`}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="YouTube Video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      />
                    )}
                    {showingMedia.type === 'video' && soundBlocked && !mediaModalVisible && (
                      <MediaSoundButton type="button" onClick={handleSoundOn}>
                        <SoundOutlined /> Tap for sound
                      </MediaSoundButton>
                    )}
                    <MediaExpandButton onClick={handleExpandMedia}>
                      <ExpandOutlined />
                    </MediaExpandButton>
                  </MediaDisplayArea>
                  <MediaQuestionText>
                    <div className="helper-text">
                      <InfoCircleOutlined /> Your mic is muted. Press "I'm ready to answer" when done viewing.
                    </div>
                    <div className="media-tips">
                      {showingMedia.type === 'video' || showingMedia.type === 'link'
                        ? 'Replay it or skip to any part with the player controls. You can also ask the interviewer to show it again later.'
                        : 'You can ask the interviewer to show it again later.'}
                    </div>
                  </MediaQuestionText>
                </>
              ) : (
                <WaveformContainer ref={waveformRef}>
                  {WAVE_BARS.map((bar, i) => (
                    <WaveformBar
                      key={i}
                      $height={bar.height}
                      style={{ background: BAR_COLORS[i] }}
                    />
                  ))}
                </WaveformContainer>
              )}
            </CallContent>

            <ActionButtonsRow>
              {isNoisy && !micMuted && (
                <NoiseBubble>
                  <span className="noise-dot" />
                  It seems there's too much disturbance - could you consider moving to a quieter place?
                </NoiseBubble>
              )}
              <ActionButtonWrapper>
                <ActionCircle onClick={handleEndCall} style={{ background: '#D94F4F', borderColor: '#D94F4F', color: '#fff' }}>
                  <PhoneOffIcon />
                </ActionCircle>
                <ActionLabel>Hangup</ActionLabel>
              </ActionButtonWrapper>

              {showingMedia && (
                <ActionButtonWrapper>
                  <ActionCircle
                    onClick={readyEnabled ? handleReady : undefined}
                    style={{
                      background: readyEnabled ? '#2E45DA' : 'rgba(46, 69, 218, 0.3)',
                      borderColor: readyEnabled ? '#2E45DA' : 'rgba(46, 69, 218, 0.3)',
                      color: '#fff',
                      cursor: readyEnabled ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {readyTimer > 0 && readyTimer <= 3 ? readyTimer : <CheckOutlined />}
                  </ActionCircle>
                  <ActionLabel>{readyTimer > 3 ? `Wait ${readyTimer}s` : readyTimer > 0 ? 'Almost...' : "I'm ready to answer"}</ActionLabel>
                </ActionButtonWrapper>
              )}

              {canSkipAhead && !showingMedia && (
                <ActionButtonWrapper>
                  <ActionCircle
                    onClick={handleSkipAhead}
                    disabled={skipCooldown}
                    aria-label="Next question"
                    style={{ background: skipCooldown ? 'rgba(46, 69, 218, 0.3)' : '#2E45DA', borderColor: skipCooldown ? 'rgba(46, 69, 218, 0.3)' : '#2E45DA', color: '#fff', cursor: skipCooldown ? 'not-allowed' : 'pointer' }}
                  >
                    <ArrowRightOutlined />
                  </ActionCircle>
                  <ActionLabel>Next question</ActionLabel>
                </ActionButtonWrapper>
              )}

              <ActionButtonWrapper style={{ position: 'relative' }}>
                {micMuted && (
                  <MutedBubble>You are muted</MutedBubble>
                )}
                <ActionCircle
                  className={micMuted ? 'muted' : ''}
                  onClick={handleToggleMute}
                  disabled={!!showingMedia}
                  style={showingMedia ? { opacity: 0.3, cursor: 'not-allowed', background: '#f0f0f0', borderColor: '#ddd', color: '#bbb' } : {}}
                >
                  {micMuted ? <MicOffIcon /> : <MicOnIcon />}
                </ActionCircle>
                <ActionLabel style={showingMedia ? { opacity: 0.3 } : {}}>{micMuted ? 'Unmute' : 'Mute'}</ActionLabel>
              </ActionButtonWrapper>
            </ActionButtonsRow>

            {/* Full-screen media preview modal */}
            <ThemeModal
              title={null}
              open={mediaModalVisible}
              onCancel={handleCloseMediaModal}
              footer={null}
              width={showingMedia?.type === 'pdf' ? 680 : '95%'}
              centered
              destroyOnClose
              closable
              className="media-preview-modal"
              style={{ maxWidth: showingMedia?.type === 'pdf' ? 680 : 1100 }}
            >
              {showingMedia?.type === 'image' && (
                <img src={showingMedia.url} alt={showingMedia.filename || 'Media'} style={{ width: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block' }} />
              )}
              {showingMedia?.type === 'video' && (
                <div style={{ position: 'relative' }}>
                  <video
                    key={`media-expand-${expandSeq}`}
                    ref={modalVideoRef}
                    src={showingMedia.url}
                    controls
                    playsInline
                    controlsList="nodownload nofullscreen"
                    disablePictureInPicture
                    style={{ width: '100%', maxHeight: '85vh', display: 'block' }}
                    onPlay={(e) => { autoplayPendingRef.current = null; if (!e.target.muted) setSoundBlocked(false); }}
                    onVolumeChange={(e) => { if (!e.target.muted && e.target.volume > 0) setSoundBlocked(false); }}
                    onLoadedMetadata={(e) => {
                      try { e.target.currentTime = expandFromRef.current.time; } catch (_) { /* keep 0 */ }
                      if (expandFromRef.current.muted) e.target.muted = true;
                      if (expandFromRef.current.wasPlaying) resumeVideo(e.target, expandFromRef.current.muted);
                    }}
                  />
                  {soundBlocked && (
                    <MediaSoundButton type="button" onClick={handleSoundOn}>
                      <SoundOutlined /> Tap for sound
                    </MediaSoundButton>
                  )}
                </div>
              )}
              {showingMedia?.type === 'link' && (
                <iframe src={showingMedia.url} style={{ width: '100%', height: '70vh', border: 'none' }} title="YouTube Video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
              )}
              {showingMedia?.type === 'pdf' && (
                <iframe src={`${showingMedia.url}#toolbar=0&navpanes=0&view=FitW`} style={{ width: '100%', height: '70vh', border: 'none', background: '#fff' }} title={showingMedia.filename || 'Document'} />
              )}
            </ThemeModal>
          </ActiveCallCard>
        ) : effectiveStep === 1 ? (
          // ── Step 1: Survey Introduction ──
          <ContentCard>
            <Title>{studyName}</Title>
            <MessageText
              dangerouslySetInnerHTML={{
                // No fallback text: an empty Study Message means this step is skipped entirely
                // (see hasIntroMessage above), so there is nothing to substitute here.
                __html: introHtml,
              }}
            />
            <NextButton onClick={handleNext}>
              Next
              <ArrowRightOutlined />
            </NextButton>
          </ContentCard>
        ) : (
          // ── Step 2: Call Instructions ──
          <ContentCard>
            <ProfileSection>
              <AIIcon>
                <AIAvatarIcon size={80} />
              </AIIcon>
              <ProfileName>{studyName}</ProfileName>
            </ProfileSection>

            {!noPinAgent && (
              <PinSection>
                <PinLabel>Your PIN</PinLabel>
                <PinBoxGroup>
                  {pin.split('').map((digit, index) => (
                    <PinBox key={index}>{digit}</PinBox>
                  ))}
                </PinBoxGroup>
              </PinSection>
            )}

            {phoneNumbers.length > 0 && agentId && (
              <InstructionsText>
                Choose how you'd like to start the interview:
              </InstructionsText>
            )}

            <CallOptionsContainer>
              {phoneNumbers.length > 0 && (
                <PhoneCallSection style={!agentId ? { gridColumn: '1 / -1' } : undefined}>
                  <WebCallTitle style={{ marginBottom: '16px' }}>Call by Phone</WebCallTitle>
                  <CityButtonGroup>
                    {phoneNumbers.map((phone, index) => (
                      <CityButton
                        key={index}
                        type="default"
                        icon={<PhoneOutlined />}
                        onClick={() => window.open(`tel:${phone.number.replace(/-/g, '')}`)}
                      >
                        {phone.label}: {phone.number}
                      </CityButton>
                    ))}
                  </CityButtonGroup>
                </PhoneCallSection>
              )}

              {phoneNumbers.length > 0 && agentId && (
                <OrDivider>
                  <span>Or</span>
                </OrDivider>
              )}

              {agentId && (
                <WebCallSection style={phoneNumbers.length === 0 ? { gridColumn: '1 / -1' } : undefined}>
                  <WebCallTitle>Call from Web</WebCallTitle>
                  <WebCallSubtitle>
                    Start directly from your browser
                  </WebCallSubtitle>

                  {(conversation.status === 'connected' || conversation.status === 'connecting') && (
                    <CallStatusIndicator $status={conversation.status}>
                      <span className="status-dot" />
                      {getCallStatusText()}
                    </CallStatusIndicator>
                  )}

                  <WebCallButton
                    className="start-call"
                    onClick={handleStartCall}
                    disabled={startingCall || conversation.status === 'connecting'}
                    loading={startingCall || conversation.status === 'connecting'}
                    icon={!startingCall && conversation.status !== 'connecting' && <AudioOutlined />}
                  >
                    {startingCall || conversation.status === 'connecting' ? 'Connecting...' : 'Start Call'}
                  </WebCallButton>
                </WebCallSection>
              )}
            </CallOptionsContainer>
          </ContentCard>
        )}
      </LandingPageWrapper>
    </LandingPageContainer>
  );
};

const mapStateToProps = (state) => ({
  getCampaignByIdState: state.campaign.getCampaignByIdState,
  getRespondentState: state.surveyRespondent.getRespondentState,
});

const mapDispatchToProps = (dispatch) => ({
  getCampaignByLink: (slug, code) => dispatch(getCampaignByLinkApi(slug, code)),
  getCampaignByLinkReset: () => dispatch(getCampaignByIdReset()),
  getRespondent: (transaction_id, sid) => dispatch(getRespondentByTransactionIdApi(transaction_id, sid)),
  getRespondentReset: () => dispatch(getRespondentReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignLandingPage);
